import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Search, Users, Activity, Zap, Megaphone, Menu, X } from 'lucide-react';
import { CommandPalette } from './CommandPalette';
import CRMManager from './admin/CRMManager';
import AdManager from './admin/AdManager';
import { db, auth } from '../firebase';
import { collection, onSnapshot, getDocs, doc, setDoc, runTransaction, serverTimestamp, getDoc } from 'firebase/firestore';

export default function SuperAdmin() {
  const [viewMode, setViewMode] = useState<'cuentas' | 'crm' | 'analiticas' | 'publicidad'>('cuentas');
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'Cliente', plan: 'Aura Connect', maxScreens: '1', slug: '', fiscalName: '', nif: '' });
  const [metrics, setMetrics] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [crm, setCrm] = useState<any[]>([]);
  const [heartbeats, setHeartbeats] = useState<any[]>([]);
  const [campaignsCount, setCampaignsCount] = useState(0);
  const navigate = useNavigate();

  const handleCreateUser = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');
      const idToken = await user.getIdToken();

      // URL-encoded body to prevent CORS preflight in sandboxed AI Studio preview
      const formData = new URLSearchParams();
      formData.append('email', newUser.email);
      formData.append('password', newUser.password);
      formData.append('token', idToken);

      const response = await fetch('/api/admin/create-client', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formData.toString()
      });

      const textData = await response.text();
      let resData;
      try {
        resData = JSON.parse(textData);
      } catch (e) {
        console.error('Non-JSON response:', textData);
        throw new Error(`Server returned non-JSON. Status: ${response.status}. Body: ${textData.substring(0, 100)}`);
      }

      if (!response.ok) throw new Error(resData?.error || 'Error al crear usuario en auth');

      const newUid = resData.uid;
      const normalizedRole = newUser.role === 'Admin' ? 'admin' : newUser.role === 'Comercial' ? 'sales' : 'client';
      const normalizedPlan = normalizedRole === 'client' ? newUser.plan : 'Interno';

      // Counter via transaction using Client SDK
      let nextId = 101;
      try {
        await runTransaction(db, async (transaction) => {
          const counterRef = doc(db, 'system', 'counters');
          const counterDoc = await transaction.get(counterRef);
          if (counterDoc.exists()) {
            nextId = (counterDoc.data()?.lastClientId || 100) + 1;
          }
          transaction.set(counterRef, { lastClientId: nextId }, { merge: true });
        });
      } catch (err) {
        console.error('Error with transaction:', err);
        // Fallback to random if transaction fails to avoid locking completely
        nextId = Math.floor(Math.random() * 900) + 100;
      }

      await setDoc(doc(db, 'users', newUid), {
        email: newUser.email,
        role: normalizedRole,
        plan: normalizedPlan,
        maxScreens: normalizedRole === 'client' ? parseInt(newUser.maxScreens) : 0,
        slug: newUser.slug,
        fiscalName: newUser.fiscalName,
        nif: newUser.nif,
        clientId: nextId,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
      });

      await setDoc(doc(db, 'clientes', newUid), {
        isZenMode: false,
        isControlsVisible: true,
        performanceMode: 'normal',
        centralTextScale: 1.0,
        remote_volume: 0,
        showTicker: true,
        clientId: nextId,
        createdAt: serverTimestamp(),
      });

      alert('Usuario creado con éxito');
      setNewUser({ email: '', password: '', role: 'Cliente', plan: 'Aura Connect', maxScreens: '1', slug: '', fiscalName: '', nif: '' });
    } catch (err) {
      console.error(err);
      alert('Error al crear el usuario');
    }
  };

  useEffect(() => {
    // Redirect to run.app domain if on a custom domain to bypass NGINX POST restrictions
    if (!window.location.hostname.includes('run.app') && !window.location.hostname.includes('localhost')) {
      // Use the known Shared App URL host
      window.location.replace(`https://prod-aura-display-3-136354148420./${window.location.pathname}${window.location.search}`);
      return;
    }

    const fetchAdminData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        try {
          const docSnap = await getDoc(doc(db, 'users', user.uid));
          if (docSnap.exists()) {
            const role = docSnap.data().role;
            setCurrentUserRole(role || '');
            if (['sales', 'Comercial'].includes(role)) {
              setViewMode('crm');
            }
          }
        } catch (e) { }

        // Use client SDK to query instead of API to avoid Permission Denied on Server
        try {
          const usersSnap = await getDocs(collection(db, 'users'));
          const usersList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setUsers(usersList as any);
        } catch (e) { console.error('Error fetching users directly:', e); }

        try {
          const crmSnap = await getDocs(collection(db, 'clientes'));
          const crmList = crmSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setCrm(crmList as any);
        } catch (e) { console.error('Error fetching crm directly:', e); }

        try {
          const heartbeatsSnap = await getDocs(collection(db, 'displays'));
          const hbList = heartbeatsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setHeartbeats(hbList as any);
        } catch (e) { console.error('Error fetching displays directly:', e); }

        // Dummy metrics, can be calculated on client side based on lists
        setMetrics({
          collections: 5,
          totalDocuments: users.length + crm.length + heartbeats.length,
          estimatedSizeGB: 0.001,
          activeUsers: users.length,
          storageUsed: 0
        });

      } catch (err) {
        console.error('Error initializing admin fetches:', err);
      }
    };

    fetchAdminData();

    // Auth state observer for reloading data if token changes
    const unsubAuth = auth.onIdTokenChanged((user) => {
      if (user) fetchAdminData();
    });

    const unsubCampaigns = onSnapshot(collection(db, 'ad_campaigns'), (snapshot) => {
      setCampaignsCount(snapshot.size);
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      unsubCampaigns();
      unsubAuth();
    };
  }, []);

  return (
    <div className="flex flex-col md:flex-row h-screen w-screen bg-zinc-950 text-zinc-100 md:overflow-hidden overflow-y-auto">
      <CommandPalette isOpen={isPaletteOpen} onClose={() => setIsPaletteOpen(false)} />

      {/* Mobile Top Bar */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-950 sticky top-0 z-50">
        <div>
          <h1 className="text-lg font-bold">Super Admin</h1>
          <p className="text-xs text-zinc-500">Alpha v0.1.2</p>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 border border-zinc-800 rounded-lg">
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Left Panel: Registration (Compact) */}
      <div className={`${isMobileMenuOpen ? 'flex' : 'hidden md:flex'} w-full md:w-[320px] border-b md:border-b-0 md:border-r border-zinc-800 p-4 flex-col gap-4 overflow-y-auto shrink-0 bg-zinc-950 z-40`}>
        <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-white">
          <ArrowLeft size={12} /> Volver al Panel
        </button>

        <div className="hidden md:block">
          <h1 className="text-lg font-bold">Super Admin</h1>
          <p className="text-xs text-zinc-500">Alpha v0.1.2 - Testing Ground</p>
        </div>

        <div className="flex-1 border-t border-zinc-800 pt-4 space-y-3">
          <h2 className="text-[10px] font-bold uppercase tracking-widest text-yellow-500">Registro Manual</h2>
          <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email usuario..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs" />
          <input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Clave temporal..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs" />

          <div className="grid grid-cols-2 gap-2">
            <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs text-zinc-400">
              <option value="Cliente">Cliente</option>
              <option value="Admin">Admin</option>
              <option value="Comercial">Comercial</option>
            </select>
            {newUser.role === 'Cliente' ? (
              <select value={newUser.plan} onChange={e => setNewUser({ ...newUser, plan: e.target.value })} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs text-zinc-400">
                <option value="Aura Connect">Aura Connect</option>
                <option value="Aura Total">Aura Total</option>
              </select>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs text-zinc-600 flex items-center">
                Uso Interno
              </div>
            )}
          </div>
          {newUser.role === 'Cliente' && (
            <input type="number" value={newUser.maxScreens} onChange={e => setNewUser({ ...newUser, maxScreens: e.target.value })} placeholder="Limitar pantallas..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs" />
          )}
          <input type="text" value={newUser.slug} onChange={e => setNewUser({ ...newUser, slug: e.target.value })} placeholder="URL Amigable (Slug)..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs" />
          <input type="text" value={newUser.fiscalName} onChange={e => setNewUser({ ...newUser, fiscalName: e.target.value })} placeholder="Nombre Fiscal..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs" />
          <input type="text" value={newUser.nif} onChange={e => setNewUser({ ...newUser, nif: e.target.value })} placeholder="NIF / CIF..." className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2.5 text-xs" />

          <button onClick={handleCreateUser} className="w-full mt-2 py-2.5 rounded-lg bg-yellow-500 text-black font-bold text-[10px] uppercase tracking-widest hover:bg-yellow-400 flex items-center justify-center gap-2">
            Crear Usuario Aura Business
          </button>
          <button className="w-full py-2.5 rounded-lg bg-zinc-800 text-white font-bold text-[10px] uppercase tracking-widest hover:bg-zinc-700">
            Descargar Factura Alta
          </button>
        </div>
        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <h2 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Herramientas Comerciales</h2>
          <a href="/aura" target="_blank" className="block p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-[10px] uppercase hover:bg-zinc-800">Aura Display Viewer</a>
          <a href="/negocio" target="_blank" className="block p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-[10px] uppercase hover:bg-zinc-800">Panel Negocio</a>
          <a href="/admin/changelog" target="_blank" className="block p-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-[10px] uppercase hover:bg-zinc-800">Changelog</a>
        </div>
      </div>

      {/* Right Panel: Content (Dense) */}
      <div className={`flex-1 flex flex-col bg-zinc-900/30 ${isMobileMenuOpen ? 'hidden md:flex' : 'flex'}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center border-b border-zinc-800 px-6 gap-4 sm:gap-0">
          <div className="flex w-full sm:w-auto overflow-x-auto custom-scrollbar">
            {(['sales', 'Comercial'].includes(currentUserRole) ? ['crm'] : ['cuentas', 'crm', 'analiticas', 'publicidad']).map(mode => (
              <button key={mode} onClick={() => { setViewMode(mode as any); setIsMobileMenuOpen(false); }} className={`py-4 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.2em] px-4 ${viewMode === mode ? 'text-white border-b border-yellow-500' : 'text-zinc-600'}`}>
                {mode}
              </button>
            ))}
          </div>
          <div className="hidden sm:flex ml-auto text-[10px] items-center gap-2 text-zinc-600">
            <kbd className="bg-zinc-900 border border-zinc-800 p-1 rounded">⌘ K</kbd> para comandos
          </div>
        </div>

        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[{ icon: Users, label: 'Cuentas', val: '13' }, { icon: Activity, label: 'Online', val: '2' }, { icon: Zap, label: 'Impulsos', val: '614' }, { icon: Megaphone, label: 'Ads', val: campaignsCount.toString() }].map((kpi, i) => (
            <div key={i} className="bg-zinc-950 p-4 rounded-xl border border-zinc-800 flex items-center gap-3">
              <div className="p-2 bg-zinc-900 rounded-lg text-zinc-400"><kpi.icon size={16} /></div>
              <div>
                <p className="text-[9px] text-zinc-600 uppercase tracking-wider">{kpi.label}</p>
                <p className="text-xl font-bold">{kpi.val}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 flex-1 overflow-y-auto">
          {viewMode === 'publicidad' && <AdManager />}

          {viewMode !== 'publicidad' && (
            <div className="mb-4 p-4 bg-zinc-950 border border-zinc-800 rounded-xl">
              <h3 className="text-xs font-bold text-zinc-500 uppercase mb-2">Salud DB (Firestore)</h3>
              {metrics ? (
                <div className="flex gap-4">
                  <p className="text-xs">Colecciones: <strong>{metrics.collections}</strong></p>
                  <p className="text-xs">Documentos: <strong>{metrics.totalDocuments}</strong></p>
                  <p className="text-xs">Est. Tamaño: <strong>{metrics.estimatedSizeGB.toFixed(4)} GB</strong></p>
                </div>
              ) : (
                <p className="text-xs text-zinc-600">Cargando métricas...</p>
              )}
            </div>
          )}

          {viewMode === 'cuentas' && (
            <div className="space-y-2">
              {users.map(user => (
                <div key={user.id} className="bg-zinc-950 p-3 rounded-lg border border-zinc-800 flex justify-between items-center text-xs">
                  <div className="flex flex-col gap-1">
                    <span>{user.email}</span>
                    <span className="text-[10px] text-zinc-500">{user.id} • {user.role || 'Cliente'}{user.createdBy && ` • Creador: ${users.find(u => u.id === user.createdBy)?.email || user.createdBy}`}</span>
                  </div>
                  <button
                    onClick={() => window.open(`/admin?uid=${user.id}`, '_blank')}
                    className="text-[10px] uppercase font-bold text-yellow-500 hover:text-yellow-400 py-1 px-3 border border-yellow-500/30 rounded-full hover:bg-yellow-500/10 transition-colors"
                  >
                    Aura Connect
                  </button>
                </div>
              ))}
            </div>
          )}

          {viewMode === 'crm' && (
            <div className="space-y-2">
              <CRMManager />
            </div>
          )}

          {viewMode === 'analiticas' && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-zinc-500 uppercase mb-2">Últimos Heartbeats (Dispositivos)</h3>
              {heartbeats.map(heartbeat => (
                <div key={heartbeat.id} className="bg-zinc-950 p-3 rounded-lg border border-zinc-800 flex justify-between items-center text-xs">
                  <span>{heartbeat.id}</span>
                  <span className="text-zinc-500">{heartbeat.lastSeen ? new Date(heartbeat.lastSeen.seconds * 1000).toLocaleString() : 'N/A'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
